import React, {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import {useGeolocalizedSizes, useNotify, useProduct, useSetUI, useUI} from 'coreHooks';
import {UIState} from 'coreStore/data/ui/types';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Link from 'brandComponents/Link';
import Popup from 'brandComponents/Popup';
import Typography from 'brandComponents/Typography';

import messages from './messages';
import {NotifyMeForm} from './Subcomponents';

import styles from './NotifyMePopup.module.scss';

const NotifyMePopupWrapper = () => {
	const notifyMeUI: UIState['notifyMe'] = useUI('notifyMe');

	if (notifyMeUI?.productID) {
		return (
			<NotifyMePopup notifyMeUI={notifyMeUI} />
		);
	} else {
		return null;
	}
};

// TODO: fix variant and replace it with Product class
const NotifyMePopup = ({notifyMeUI}) => {
	const {managerRepository} = useGeneralProvider();
	const popupsMgr = managerRepository.getPopupsMgr();
	const [notifyMeProduct, setNotifyMeProduct] = useState(null);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const intl = useIntl();
	const setUI = useSetUI();
	const notify = useNotify();
	const product = useProduct(notifyMeUI?.productID);
	const geolocalizedSizes = useGeolocalizedSizes(product);

	useEffect(() => {
		if (notifyMeUI?.selectedVariationID) {
			popupsMgr.openPopup('notifyMe');
		} else {
			popupsMgr.closePopup('notifyMe');
		}
	}, [notifyMeUI?.selectedVariationID]);

	useEffect(() => {
		if (product.getProductId()) {
			setNotifyMeProduct(product);
		}
	}, [product.getProductId()]);

	useEffect(() => {
		if (notifyMeUI?.selectedVariationID && notifyMeProduct?.id) {
			setSelectedVariant(notifyMeProduct.getVariant(notifyMeUI.selectedVariationID));
		}
	}, [notifyMeProduct?.id, notifyMeUI?.selectedVariationID]);

	const onError = useCallback(() => {
		setUI.hideNotifyMePopup();
		notify.error(intl.formatMessage(messages.generalError));
	}, []);

	const onSuccess = useCallback(() => {
		setUI.hideNotifyMePopup();
		notify.success(intl.formatMessage(messages.notifyMeSuccess));
	}, []);

	const handleClose = useCallback(() => {
		setUI.hideNotifyMePopup();
	}, []);

	return (
		<Popup id="notifyMe" onClose={handleClose} overlayClassName={styles['notify-me-popup']}>
			<Typography classNames={styles['notify-me-popup__title']} variant="h3">
				{intl.formatMessage(messages.title)}
			</Typography>
			<Typography classNames={styles['notify-me-popup__description']} variant="p">
				{intl.formatMessage(messages.description)}
			</Typography>
			<Typography classNames={styles['notify-me-popup__product']} variant="p">
				{notifyMeProduct?.name && <span>{notifyMeProduct.name}</span>}
			</Typography>
			{selectedVariant && (
				<>
					<div className={styles['notify-me-popup__variation']}>
						<div>
							<span className={styles['notify-me-popup__variation-key']}>{intl.formatMessage(messages['size'])}:</span>
							<span className={styles['notify-me-popup__variation-value']}>{geolocalizedSizes[notifyMeUI?.selectedVariationID]}</span>
						</div>
						<div>
							<span className={styles['notify-me-popup__variation-key']}>{intl.formatMessage(messages['color'])}:</span>
							<span className={styles['notify-me-popup__variation-value']}>{selectedVariant.getColorDescription()}</span>
						</div>
					</div>
					<NotifyMeForm
						showLabel={false}
						submitBtnText={intl.formatMessage(messages.submitBtnText)}
						classNames={{submitButton: styles['notify-me-popup__submit-btn']}}
						pid={selectedVariant.getProductId()}
						onSuccess={onSuccess}
						onError={onError}
					/>
					<Typography classNames={styles['notify-me-popup__privacy-policy']} variant="p">
						{intl.formatMessage(messages.privacyPolicyText, {
							cta: (chunk) => (
								<Link to="/privacy-policy" onClick={handleClose}>
									{chunk}
								</Link>
							),
						})}
					</Typography>
				</>
			)}
		</Popup>
	);
};

export default NotifyMePopupWrapper;
